<template>
  <div id="tour" class="container-fluid pb-32 pb-lg-64 case-v2-wide">
    <v-loader v-if="status === 'loading'" />

    <template v-else-if="status === 'loaded' && !!clientCase">
      <div class="pt-8 pb-4 mb-12 mt-16">
        <div class="row align-items-center">
          <div class="col-md-5">
            <div class="h4 ff-secondary mb-0 d-flex align-items-center case-title">
              <v-icon icon="compass" size="xl" class="icon-accent-primary me-8" />
              {{ $t(getTitle()) }}
            </div>
            <div class="text-sm fw-semibold text-dark mt-2 ms-24 case-subtitle">
              {{ clientCase?.name }}
            </div>
          </div>
          <div class="col-md-7 d-flex justify-content-end gap-8" v-if="pricingSection === 'case.pricing.show'">
            <v-button
              :disabled="status === 'loading' || userStore.caseIsSold"
              class="btn-icon w-fit align-self-end rounded-sm text-uppercase x-small fw-bold px-20 shadow-xs case-action-btn action-sold"
              size="sm"
              variant="primary"
              @click="triggerSold"
            >
              <v-icon icon="check" size="sm" space="me-8" class="icon-accent-yellow" />
              {{ $t("tourBooking.sold") }}
            </v-button>

            <v-button
              :disabled="status === 'loading' || userStore.caseIsSold"
              class="btn-icon w-fit align-self-end rounded-sm text-uppercase x-small fw-bold px-20 shadow-xs case-action-btn action-cancel"
              size="sm"
              variant="warning"
              @click="triggerCancel"
            >
              <v-icon icon="close" size="xs" space="me-8" class="icon-accent-orange" />
              {{ $t("tourBooking.cancel") }}
            </v-button>
          </div>
          <div class="col-md-7 d-flex justify-content-end gap-8" v-else>
            <v-button
              v-if="!isPreCase"
              :disabled="status === 'loading' || userStore.caseIsSold"
              class="btn-icon w-fit align-self-end case-action-btn action-connect"
              size="sm"
              variant="primary"
              @click="connectLuggage"
            >
              {{ $t("tourClient.connectLuggage") }}
              <v-icon icon="luggage-cart" size="sm" space="ms-8" class="icon-accent-neutral" />
            </v-button>

            <v-button
              v-if="!isPreCase"
              :disabled="status === 'loading' || userStore.caseIsSold"
              class="btn-icon w-fit align-self-end case-action-btn action-export"
              size="sm"
              variant="primary"
              @click="exportAsTemplate"
            >
              {{ $t("tourBuilder.exportAsProduct") }}
              <v-icon icon="export-arrow" size="sm" space="ms-8" class="icon-accent-white" />
            </v-button>

            <v-button
              class="btn-icon w-fit align-self-end case-action-btn action-logs"
              size="sm"
              variant="primary"
              @click="caseLogsModal = true"
            >
              {{ $t("tourClient.caseLogs") }}
              <v-icon icon="history" size="sm" space="ms-8" />
            </v-button>

          </div>
        </div>
      </div>

      <div
        v-if="status === 'loaded' && !!tourIdAux"
        class="tour-steps-container d-flex align-items-center justify-content-center flex-wrap"
      >
        <div v-for="step in filteredTourSteps" :key="step.key" class="tour-step">
          <router-link
            v-if="step.key !== 'preview'"
            :id="step.id"
            :aria-label="$t(step.translation)"
            :class="{ 'router-link-active': step.url === $route.name }"
            :to="{
              name: step.url,
              params: getParams(step.url),
              query: step.query,
            }"
            class="btn btn-outline-primary btn-sm py-2"
            @click="actualStep = step.key"
          >
            {{ $t(step.translation) }}
          </router-link>

          <a
            v-if="step.key === 'preview'"
            :id="step.id"
            :aria-label="$t(step.translation)"
            :class="{ 'router-link-active': step.url === $route.name }"
            class="btn btn-outline-primary btn-sm py-2"
            href="#"
            @click="openPreview(step)"
          >
            {{ $t(step.translation) }}
          </a>
        </div>
      </div>
      <div class="alert alert-danger precase-sold-alert" v-if="clientCase?.is_sold" role="alert">
        <v-icon icon="info" size="sm" space="me-8" class="icon-accent-white" />
        <template v-if="clientCase?.is_this_pre_case_sold">
          {{ $t("general.soldPre") }}
        </template>
        <template v-else>
          {{ $t("general.soldVersion") }}
        </template>
        {{ "  " }}
        <router-link
          :to="{
            name: 'case-v2.setup.show',
            params: { caseId: clientCase?.new_case_id },
          }"
          class="fw-semibold text-decoration-underline precase-sold-alert__link"
        >
        {{ $t("general.soldLink") }}
        </router-link>
      </div>

      <router-view v-slot="{ Component }">
        <component
          :is="Component"
          v-if="!!clientCase"
          ref="pricingRef"
          :client-tour="clientCase"
          @reload-client-tour="loadClientCase()"
        />
      </router-view>
    </template>

    <template v-else>
      <div class="text-center py-64 border rounded shadow-sm bg-white mt-24">
        <v-icon icon="error_outline" size="4xl" class="text-danger mb-16" />
        <h4 class="fw-bold text-dark">{{ $t("general.error.somethingWentWrong") }}</h4>
        <p class="text-muted mb-24">Failed to load case data or case not found.</p>
        <v-button variant="primary" size="sm" @click="loadClientCase">
          <v-icon icon="refresh" size="xs" space="me-8" />
          Retry
        </v-button>
      </div>
    </template>

    <the-case-setup-quote-off-canvas-v2
      v-if="offCanvasToShow === 'the-case-setup-quote-off-canvas'"
      :opened-window="openedWindow"
      :quote-route="quoteRoute"
      @closed="closeOffCanvas"
      @set-opened-window="openedWindow = $event"
    />

    <the-case-duplicate-off-canvas-v2
      v-if="offCanvasToShow === 'the-case-duplicate-off-canvas'"
      :case-id="caseId"
      :client-id="clientCase?.client_id"
      :client-name="clientCase?.client.name"
      :tour-id="tourId"
      @closed="closeOffCanvas"
      version="new"
    />

    <TheCaseDuplicateModal
      v-if="copyCaseModal"
      :agency="agency.slug"
      :case-id="caseId"
      @closed="copyCaseModal = false"
    />

    <TheCaseLogsModal
      v-if="caseLogsModal"
      :logs="(clientCase as any)?.activity_logs"
      @closed="caseLogsModal = false"
    />

    <TheCaseConnectLuggage
      v-if="caseConnectLuggage"
      :agency="agency.slug"
      :case-id="caseId"
      @loadClientCases="loadClientCase"
      @closed="caseConnectLuggage = false"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import api from "@/api";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import { useAuthStore } from "@/stores/auth";
import ClientCase from "@/api/objects/ClientTour";
import Tour from "@/api/objects/Tour";
import { useUserStore } from "@/stores/user";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
import TheCaseSetupQuoteOffCanvasV2 from "@/components/case/parts/V2/TheCaseSetupQuoteOffCanvasV2.vue";
import TheCaseDuplicateOffCanvasV2 from "@/components/case/parts/V2/TheCaseDuplicateOffCanvasV2.vue";
import TheCaseDuplicateModal from "@/components/case/V2/TheCaseDuplicateModal.vue";
import TheCaseLogsModal from "@/components/case/V2/TheCaseLogsModal.vue";
import TheCaseConnectLuggage from "@/components/case/V2/TheCaseConnectLuggage.vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n, I18nT } from "vue-i18n";
import { useToast } from "vue-toastification";

const props = defineProps({
  caseId: {
    type: String,
    required: true,
  },
  tourId: {
    type: String,
    required: false,
  },
});

const userStore = useUserStore();
const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const toast = useToast();
const { offCanvasToShow, openOffCanvas, closeOffCanvas } = useOffCanvasUtils();

const { agency } = storeToRefs(authStore);
const { currentTour } = storeToRefs(userStore);

const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;

const openedWindow = ref<any>(null);
const openedWindowPreview = ref<any>(null);
const routerChild = ref<any>(null);
const pricingRef = ref<any>(null);
const clientCase = ref<ClientCase | null>(null);
const tourIdAux = ref<number | string | null>(null);
const tours = ref<Tour[]>([]);
const status = ref<string>("loading");
const actualStep = ref<string>("setup");
const quoteRoute = reactive<Record<string, any>>({});
const pricingSection = ref<string>("");
const copyCaseModal = ref<boolean>(false);
const caseLogsModal = ref<boolean>(false);
const caseConnectLuggage = ref<boolean>(false);
const isUnmounted = ref(false);

const isWebBookingCase = computed(() => {
  return Number(clientCase.value?.is_web_booking) === -1 || Number(clientCase.value?.is_web_booking) === 1;
});

const isPreCase = computed(() => {
  return Number(clientCase.value?.is_web_booking) === -1;
});

const defaultLocale = computed(() => {
  return import.meta.env.VITE_APP_DEFAULT_LOCALE || "en";
});

const tourSteps = computed(() => {
  const language = localStorage.getItem("language") || defaultLocale.value;

  const steps = [
    {
      key: "setup",
      url: "case-v2.setup.show",
      id: "case-v2-setup",
      translation: "tourClient.setup",
    },
    {
      key: "builder",
      url: "case.builder.show",
      id: "tour-builder",
      translation: "tourClient.builder",
    },
    {
      key: "pricing",
      url: "case.pricing.show",
      id: "tour-pricing",
      translation: "tourClient.pricing",
    },
    {
      key: "preview",
      url: "case.preview.public",
      id: "tour-preview",
      translation: "tourClient.preview",
    },
    {
      key: "billing-invoice",
      url: "case.billing-invoice.show",
      id: "tour-preview-quote",
      translation: "general.shared.billing",
    },
  ];

  return steps;
});

const filteredTourSteps = computed(() => {
  if (status.value === "loading" || !clientCase.value) return [];

  return tourSteps.value.filter((step) => {
    if (step.key === "pricing" && agency.value?.isCollaborator) return false;

    if (isPreCase.value && ["builder", "preview"].includes(step.key)) {
      return false;
    }

    return true;
  });
});

const loadClientCase = async () => {
  if (!!agency.value) {
    try {
      status.value = "loading";
      pricingSection.value = String(route.name || "");

      await userStore.setCurrentCase(agency.value.slug, Number(props.caseId));

      if (isUnmounted.value) return;

      const data = currentTour.value;
      if (!data) return;

      clientCase.value = data;

      userStore.setCaseIsDraft(data?.is_draft);
      userStore.setCaseIsSold(data?.is_sold);
      tourIdAux.value = clientCase.value?.activity_id;

      if (route.name) {
        await router.replace({
          name: route.name,
          params: {
            caseId: Number(props.caseId),
          },
        });
      } else {
        await router.replace({
          name: "case-v2.setup.show",
          params: {
            caseId: Number(props.caseId),
          },
        });
      }
    } catch (e) {
      console.error(e);
      if (!isUnmounted.value) {
        toast.error(e?.response?.data?.message || t("general.error.somethingWentWrong"));
      }
    } finally {
      if (!isUnmounted.value) {
        status.value = "loaded";
      }
    }
  }
};

watch(
  [() => props.caseId, () => agency.value],
  async ([newCaseId, newAgency]) => {
    if (newCaseId && newAgency) {
      await loadClientCase();
    }
  },
  { immediate: true }
);

// watch route change
watch(
  () => route.name,
  (newName, oldName) => {
    if (newName !== oldName) {
      pricingSection.value = newName?.toString() || "";
    }
  }
);

onMounted(async () => {
  // Data loading is handled by the immediate watch on caseId
});

const triggerSold = () => {
  if (pricingRef.value) {
    if (typeof pricingRef.value.soldTour === "function") {
      pricingRef.value.soldTour();
    }
  }
};

onUnmounted(() => {
  isUnmounted.value = true;
  userStore.setCurrentTour(null);
});

const triggerCancel = () => {
  if (pricingRef.value) {
    if (typeof pricingRef.value.cancelTour === "function") {
      pricingRef.value.cancelTour();
    }
  }
};

const createTour = async () => {
  await $modal?.confirm({
    title: t("tourClient.createTour"),
    text: t("tourClient.createTourText"),
    leftButtonClasses: "btn-tertiary",
    confirmButtonText: t("general.button.confirm"),
    confirmButtonCallback: doCreateTour,
  });
};

const doCreateTour = async () => {
  if (!!agency.value) {
    try {
      const tour = await api.tour.create(agency.value.slug, Number(props.caseId), {
        status: "initializing",
      });

      toast.success("Changes saved"); // or use i18n like `useI18n().t('general.shared.savedChanges')`

      await loadClientCase();

      await router.replace({
        name: "case.builder.show",
        params: {
          caseId: Number(props.caseId),
          tourId: tour.id,
        },
      });
    } catch (e) {
      console.error(e);
    }
  }
};

const archiveTour = async (tourId: string | number) => {
  await $modal?.delete({
    title: t("tourClient.archiveTour"),
    text: t("tourClient.archiveTourText"),
    deleteButtonText: t("general.button.archive"),
    deleteButtonLoadingText: t("general.button.archiving"),
    rightButtonClasses: "btn-tertiary",
    deleteButtonCallback: () => doArchiveTour(tourId),
  });
};

const doArchiveTour = async (tourId: string | number) => {
  if (!!agency.value) {
    try {
      await api.tour.archive(agency.value.slug, props.caseId, tourId);

      toast.success(t("general.shared.savedChanges"));

      await loadClientCase();

      // Optional redirect
      // await router.replace({
      //   name: 'tour.builder.show',
      //   params: { caseId: Number(props.caseId), tourId: tour.id },
      // });
    } catch (e) {
      console.error(e);
    }
  }
};

const exportAsTemplate = async () => {
  await $modal?.confirm({
    title: t("tourBuilder.exportTourAsTemplateTitle"),
    text: t("tourBuilder.exportTourAsTemplateText"),
    leftButtonClasses: "btn-tertiary",
    confirmButtonCallback: doExportAsTemplate,
  });
};

const doExportAsTemplate = async () => {
  if (agency.value && clientCase.value) {
    try {
      await api.case.caseExportAsActivity(agency.value.slug, clientCase.value.activity_id);
      toast.success(t("general.shared.savedChanges"));
    } catch (e: any) {
      console.error(e?.response?.data);
      toast.error(e?.response?.data?.message || t("general.shared.errorMessage"));
    }
  }
};

const connectLuggage = () => {
  caseConnectLuggage.value = true;
};

const getTitle = () => {
  switch (actualStep.value) {
    case "setup":
      return "tourClient.case";
    case "builder":
      return "tourClient.tourBuilder";
    case "pax":
      return "tourClient.tourPax";
    case "contacts":
      return "tourClient.tourContacts";
    case "bookings":
      return "tourClient.tourStatuses";
    case "pricing":
      return "tourClient.tourPricing";
    case "billing-invoice":
      return "general.shared.billing";
    default:
      return "tourClient.tourBuilder";
  }
};

const getParams = (url: string) => {
  if (url === "case-v2.index") {
    return {};
  }

  return {
    caseId: props.caseId,
    tourId: props.tourId,
  };
};

const openQuote = (step: Record<string, any>) => {
  console.log(step);

  quoteRoute.value = step;
  openOffCanvas("the-case-setup-quote-off-canvas");
};

const openPreview = (step: Record<string, any>) => {
  if (openedWindowPreview.value && !openedWindowPreview.value.closed) {
    openedWindowPreview.value.close();
  }

  const routeData = router.resolve({
    name: step.url,
    params: { caseId: props.caseId },
  });

  const topWindow = window.top ?? window;
  const width = 1200;
  const height = 900;
  const top = topWindow.outerHeight / 2 + topWindow.screenY - height / 2;
  const left = topWindow.outerWidth / 2 + topWindow.screenX - width / 2;

  openedWindowPreview.value = window.open(
    routeData.href,
    "_blank",
    `scrollbars=yes,resizable=yes,status=no,location=no,toolbar=no,menubar=no,left=${left},width=${width},height=${height},top=${top}`
  );
};

onUnmounted(() => {
  userStore.setCurrentTour(null);
});
</script>

<style lang="scss" scoped>
.case-v2-wide {
  max-width: 1550px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1.4rem;
  padding-right: 1.4rem;
}

@media (min-width: 1400px) {
  .case-v2-wide {
    padding-left: 2rem;
    padding-right: 2rem;
  }
}

.case-title {
  font-size: 1.75rem !important;
  font-weight: 700 !important;
}

.case-subtitle {
  font-size: 1.2rem !important;
  font-weight: 600 !important;
}

.tour-steps-container {
  gap: 1rem;
  justify-content: center !important;
  margin-bottom: 0 !important;
}

/* Precase "sold" alert banner — visually separated from tour steps above */
.precase-sold-alert {
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
  padding: 0.85rem 1.25rem;
  border-radius: 10px;
  border: none;
  background-color: #f32e2e;
  color: #ffffff;
  font-size: 0.95rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  box-shadow: 0 2px 8px rgba(243, 46, 46, 0.25);
}

.precase-sold-alert__link {
  color: #ffffff !important;
}

.precase-sold-alert__link:hover {
  color: #ffe5e5 !important;
}

.tour-step {
  .btn {
    border-radius: 8px;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.2px;
    padding: 0.55rem 1rem;
    background-color: #ffffff;
    color: #2c2b2b;
  }

  .router-link-active {
    background-color: #e61275 !important;
    border-color: #e61275 !important;
    color: #ffffff !important;
  }
}

.case-action-btn {
  border-radius: 6px !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  letter-spacing: 0.35px !important;
  padding: 0.58rem 1.05rem !important;
  min-height: 40px;
}

.action-sold {
  background-color: #57C68A !important;
  border-color: #57C68A !important;
  color: #ffffff !important;
}

.action-sold:hover {
  background-color: #45b478 !important;
  border-color: #45b478 !important;
}

.action-cancel {
  background-color: #f32e2e !important;
  border-color: #ff543e !important;
  color: #222422 !important;
}

.action-cancel:hover {
  background-color: #e58f45 !important;
  border-color: #e58f45 !important;
}

.action-connect {
  background-color: #E2C92E !important;
  border-color: #E2C92E !important;
  color: #222422 !important;
}

.action-connect:hover {
  background-color: #d3ba1f !important;
  border-color: #d3ba1f !important;
}

.action-export {
  background-color: #FD0C6F !important;
  border-color: #FD0C6F !important;
  color: #ffffff !important;
}

.action-export:hover {
  background-color: #dc095f !important;
  border-color: #dc095f !important;
}

.action-copy,
.action-duplicate,
.action-logs {
  background-color: #222422 !important;
  border-color: #222422 !important;
  color: #ffffff !important;
}

.action-copy:hover,
.action-duplicate:hover,
.action-logs:hover {
  background-color: #171917 !important;
  border-color: #171917 !important;
}

:deep(.case-details .h6),
:deep(.accordion-header .h6) {
  font-size: 1.22rem !important;
}

:deep(.accordion-subtitle) {
  font-size: 0.95rem !important;
  line-height: 1.3 !important;
}

:deep(.v-form-label),
:deep(.label span),
:deep(.form-label) {
  font-size: 0.86rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.35px !important;
}

:deep(.form-control),
:deep(.vs__selected),
:deep(.vs__dropdown-option) {
  font-size: 1.04rem !important;
  font-weight: 500 !important;
  min-height: 40px !important;
}

:deep(.table) {
  font-size: 1rem !important;
  font-weight: 500 !important;
}

:deep(.table thead th) {
  font-size: 0.86rem !important;
  font-weight: 700 !important;
}

:deep(.btn-icon:not(.btn-view)) {
  font-size: 13px !important;
}

:deep(.btn:not(.rounded-circle)) {
  border-radius: 6px !important;
}

:deep(.btn-icon:not(.rounded-circle):not(.btn-view)) {
  border-radius: 6px !important;
}

:deep(.btn-primary) {
  background-color: #fd0c6f;
  border-color: #fd0c6f;
}

:deep(.btn-primary:hover) {
  background-color: #dc095f;
  border-color: #dc095f;
}

:deep(.btn-outline-primary) {
  color: #2c2b2b;
  border-color: #e6b328;
  background-color: #ffffff;
}

:deep(.btn-outline-primary:hover),
:deep(.btn-outline-primary:focus),
:deep(.btn-outline-primary.router-link-active) {
  background-color: #e61275 !important;
  border-color: #e61275 !important;
  color: #ffffff !important;
}

/* Language tabs: black underlines and borders for all locales */
:deep(.translations-tab .nav-tabs) {
  border-bottom: 0 !important;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px;
  background: #f3f4f6;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 999px;
}

:deep(.translations-tab .nav-tabs .nav-tab-link) {
  border: 0 !important;
  border-radius: 999px !important;
  box-shadow: none !important;
  margin-top: 0 !important;
  color: #5c5c5c !important;
  background-color: transparent !important;
  font-weight: 700;
}

:deep(.translations-tab .btn-outline-primary) {
  border: 0 !important;
  background-color: transparent !important;
}

:deep(.translations-tab .btn-outline-primary.active),
:deep(.translations-tab .btn-outline-primary:hover),
:deep(.translations-tab .btn-outline-primary:focus) {
  background-color: #ffffff !important;
  color: #111827 !important;
  border: 0 !important;
  box-shadow: 0 1px 1px rgba(17, 24, 39, 0.08) !important;
}

:deep(.translations-tab .btn-outline-primary.active) {
  border-bottom: 0 !important;
}

::deep(.translations-tab .btn-outline-primary:hover:not(.active)) {
  background-color: rgba(255, 255, 255, 0.7) !important;
  color: #111827 !important;
}

/* Case V2: keep translation tabs aligned with Activity style */
::deep(.translations-tab) {
  .btn-outline-primary {
    border: 0 !important;
    background-color: transparent !important;
  }

  .btn-outline-primary.active,
  .btn-outline-primary:focus {
    background-color: #ffffff !important;
    color: #111827 !important;
    border: 1px solid rgba(17, 24, 39, 0.12) !important;
    box-shadow: 0 1px 2px rgba(17, 24, 39, 0.08) !important;
    border-bottom: 1px solid rgba(17, 24, 39, 0.12) !important;
  }

  .btn-outline-primary:hover:not(.active) {
    background-color: rgba(255, 255, 255, 0.7) !important;
    color: #111827 !important;
  }
}

::deep(.translations-tab .btn-outline-primary.active) {
  border-bottom: 1px solid rgba(17, 24, 39, 0.12) !important;
  margin-bottom: 0 !important;
}

/* Override any global router-link-active black styling for case step tabs */
.tour-steps-container :deep(.btn.router-link-active),
.tour-steps-container :deep(.btn.router-link-exact-active) {
  background-color: #e61275 !important;
  border-color: #e61275 !important;
  color: #ffffff !important;
  box-shadow: 0 2px 6px rgba(230, 18, 117, 0.25) !important;
}

.case-v2-wide {
  @media (max-width: 991.98px) {
    width: 100%;
    max-width: 100%;
    padding-right: var(--uq-gutter-x, 1rem);
    padding-left: var(--uq-gutter-x, 1rem);
    margin-right: 0;
    margin-left: 0;
  }
}
</style>
